import React, { useState, useEffect, useRef } from "react";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Menu } from "primereact/menu";
import { OverlayPanel } from "primereact/overlaypanel";
import ReusableTable from "../../dashboard/ReusableTable";
import ReusableForm from "../../common/ReusableForm";
import ReusableDeleteDialog from "../../common/ReusableDeleteDialog";
import { getRequest, postRequest } from "../../services/apiService";
import { Trash2, Edit, X } from 'lucide-react';

const ActionCell = ({ rowData, onEdit, onDelete }) => {
  const op = useRef(null);
  const menuItems = [
    {
      label: "Edit",
      icon: "pi pi-pencil",
      command: () => {
        op.current.hide();
        onEdit(rowData);
      }
    },
    {
      label: "Delete",
      icon: "pi pi-trash",
      command: () => {
        op.current.hide();
        onDelete(rowData);
      }
    }
  ];

  return (
    <>
      <Button
        icon="pi pi-ellipsis-h"
        className="p-button-rounded p-button-text"
        onClick={(e) => op.current.toggle(e)}
      />
      <OverlayPanel ref={op} className="p-0 shadow-lg">
        <Menu model={menuItems} className="border-none" />
      </OverlayPanel>
    </>
  );
};

const normalizeResponse = (data) => {
  if (Array.isArray(data)) return data;
  if (data.results && Array.isArray(data.results.data)) return data.results.data;
  if (data.data && Array.isArray(data.data)) return data.data;
  return [];
};

const ShipyardMaster = () => {
  const [shipyards, setShipyards] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    status_db: 1,
  });

  // Delete confirmation form fields
  const deleteFormFields = [
    {
      name: "confirmation",
      label: "Confirm Deletion",
      type: "text",
      required: true,
      placeholder: "Type 'DELETE' to confirm",
      helpText: "This action cannot be undone. Type 'DELETE' to confirm deletion."
    }
  ];

  // Delete form state
  const [deleteFormData, setDeleteFormData] = useState({
    confirmation: ""
  });

  const [editMode, setEditMode] = useState(false);
  const [shipyardToDelete, setShipyardToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const toast = useRef(null);

  // Pagination state
  const [totalRecords, setTotalRecords] = useState(0);
  const [lazyState, setLazyState] = useState({
    first: 0,
    rows: 10,
    page: 1,
  });
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const searchTimeout = useRef(null);

  const formFields = [
    { name: 'name', label: 'Shipyard Name', type: 'text', required: true },
    { name: 'country', label: 'Country', type: 'text', required: true },
    { name: 'location', label: 'Location', type: 'text', required: true },
    { name: 'established_year', label: 'Established Year', type: 'number', required: true },
    { name: 'contact_person', label: 'Contact Person', type: 'text', required: true },
    { name: 'contact_email', label: 'Contact Email', type: 'text', required: true },
    { name: 'contact_phone', label: 'Contact Phone', type: 'text', required: true },
    { name: 'website', label: 'Website', type: 'text' }
  ];

  // Fetch shipyards function
  const fetchShipyards = async () => {
    setLoading(true);
    try {
      const response = await getRequest(`/master/shipyards/?search=${encodeURIComponent(search)}&page=${lazyState.page}`);
      const shipyardsArray = normalizeResponse(response);
      setShipyards(shipyardsArray);
      setTotalRecords(response.count || 0);
    } catch (error) {
      showNotification('Failed to fetch shipyards', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Update useEffect to use fetchShipyards
  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      fetchShipyards();
    }, 700);
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [search, lazyState.page]);

  const handleTableSearch = (value) => {
    setSearch(value);
  };

  const onPageChange = (event) => {
    setLazyState(event);
  };

  const showNotification = (message, type) => {
    toast.current.show({
      severity: type,
      summary: type === 'success' ? 'Success' : type === 'warning' ? 'Warning' : 'Error',
      detail: message,
      life: 3000
    });
  };

  // Centralized function to close and reset the add/edit form modal
  const closeFormModal = () => {
    setShowDialog(false);
    setEditMode(false);
    setFormData({
      name: "",
      country: "",
      location: "",
      established_year: null,
      contact_person: "",
      contact_email: "",
      contact_phone: "",
      website: "",
      status_db: 1,
    });
  };

  const handleSave = async () => {
    try {
      // Always use POST with full payload including ID
      const payload = { ...formData };
      await postRequest('/master/shipyards/', payload);
      
      showNotification(
        editMode 
          ? 'Shipyard updated successfully' 
          : 'Shipyard added successfully', 
        'success'
      );
      
      await fetchShipyards(); // Refresh table after save
      closeFormModal(); // Use centralized close/reset function
    } catch (error) {
      console.error("Error saving shipyard:", error);
      showNotification('Failed to save shipyard', 'error');
    }
  };

  // Function to open the modal for adding a new shipyard
  const openAddModal = () => {
    closeFormModal(); // Ensure everything is reset
    setEditMode(false);
    setShowDialog(true);
  };

  const openEdit = (data) => {
    setEditMode(true);
    setFormData(data);
    setShowDialog(true);
  };

  const openDeleteModal = (shipyard) => {
    setShipyardToDelete(shipyard);
    setDeleteFormData({ confirmation: "" });
    setShowDeleteModal(true);
  };

  const deleteShipyard = async () => {
    if (deleteFormData.confirmation !== "DELETE") {
      showNotification("Please type 'DELETE' to confirm", 'error');
      return;
    }

    try {
      // Use POST with status_db=3 for soft delete
      await postRequest(`/master/shipyards/`, {
        id: shipyardToDelete.id,
        status_db: 3
      });
      
      showNotification('Shipyard deleted successfully', 'success');
      setShowDeleteModal(false);
      setShipyardToDelete(null);
      setDeleteFormData({ confirmation: "" });
      await fetchShipyards(); // Refresh table after delete
    } catch (error) {
      console.error("Error deleting shipyard:", error);
      showNotification('Failed to delete shipyard', 'error');
    }
  };

  const columns = [
    {
      field: "actions",
      header: "Actions",
      body: (rowData) => (
        <ActionCell
          rowData={rowData}
          onEdit={openEdit}
          onDelete={openDeleteModal}
        />
      ),
      sortable: false,
      notFilterable: true
    },
    { field: "name", header: "Name", sortable: true },
    { field: "country", header: "Country", sortable: true },
    { field: "location", header: "Location", sortable: true },
    // { field: "established_year", header: "Established Year", sortable: true },
    { field: "contact_person", header: "Contact Person", sortable: true },
    { field: "contact_email", header: "Contact Email", sortable: true },
    { field: "contact_phone", header: "Contact Phone", sortable: true },
    { field: "website", header: "Website", sortable: true },
    {
      field: "status_db",
      header: "Status",
      body: (rowData) => (rowData.status_db === 1 ? "Active" : "Inactive"),
      sortable: true
    },
  ];

  
  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <Toast ref={toast} position="top-right" />
      <div className="max-w-full mx-auto">
        <ReusableTable
          title="Shipyard Directory"
          data={shipyards}
          columnFields={columns}
          rows={lazyState.rows}
          colorScheme={{
            headerBgColor: '#1e3a8a',
            headerTextColor: '#ffffff',
            tableBgColor: '#ffffff',
            tableTextColor: '#334155',
            buttonBgColor: '#3b82f6',
            buttonTextColor: '#ffffff',
            deleteButtonColor: '#ef4444',
            addButtonColor: '#10b981',
            uploadButtonColor: '#f59e0b',
            exportButtonColor: '#6366f1'
          }}
          setAddNewDialogVisible={openAddModal}
          totalRecords={totalRecords}
          onPageChange={onPageChange}
          first={lazyState.first}
          loading={loading}
          searchValue={search}
          onSearchChange={handleTableSearch}
        />

        {/* Add/Edit Modal using ReusableForm with explicit modal wrapper */}
        {showDialog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white rounded-xl shadow-2xl w-full mx-auto max-w-2xl animate-fade-in">
              <ReusableForm
                headerText={editMode ? "Edit Shipyard" : "Add Shipyard"}
                formData={formData}
                setFormData={setFormData}
                fields={formFields}
                onSubmit={handleSave}
                onHide={closeFormModal} 
                loading={loading}
              />
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <ReusableDeleteDialog
          visible={showDeleteModal}
          onHide={() => setShowDeleteModal(false)}
          onDelete={deleteShipyard}
          fields={deleteFormFields}
          formData={deleteFormData}
          setFormData={setDeleteFormData}
          loading={loading}
          title="Delete Shipyard"
          description={`Are you sure you want to delete shipyard "${shipyardToDelete?.name}"? This action cannot be undone. Type 'DELETE' to confirm.`}
          deleteLabel="Delete"
        />
      </div>
    </div>
  );
};

export default ShipyardMaster;